import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { getNavItems, getUserNavSections, LogOut, Menu, X } from '../../support/iconMappings';
import { isUser } from '../../support/accountFunction';
import { UserContextPanel } from './UserContextPanel';
import { useAuth } from '../../contexts/AuthContext';

const drawerLinkClass = ({ isActive }) =>
  `flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors ${
    isActive
      ? 'bg-forge-50 text-forge-700 font-medium'
      : 'text-content-secondary hover:bg-slate-50 hover:text-content-primary'
  }`;

function MobileNav({ user, isMultiAgent = false, onLogout, currentMembership = null }) {
  const { switchAccount } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const panelRef = useRef(null);
  const menuButtonRef = useRef(null);

  const isUserLayout = isUser(user);
  const { primary, secondary } = isUserLayout
    ? getUserNavSections(user, { isMultiAgent, currentMembership })
    : { primary: [], secondary: [] };
  const adminItems = isUserLayout ? [] : getNavItems(user, { isMultiAgent, currentMembership });

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    close();
  }, [location.pathname, close]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        close();
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    const firstLink = panelRef.current?.querySelector('a, button');
    firstLink?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, close]);

  const hasNav = isUserLayout
    ? primary.length > 0 || secondary.length > 0
    : adminItems.length > 0;

  if (!hasNav) {
    return null;
  }

  const handleWorkspaceSelect = async (accountId) => {
    close();
    await switchAccount(accountId);
  };

  const renderNavLinks = (items) =>
    items.map(({ to, label, icon: Icon }) => (
      <NavLink
        key={to}
        to={to}
        className={drawerLinkClass}
        onClick={close}
      >
        <Icon className="h-5 w-5 shrink-0" aria-hidden />
        {label}
      </NavLink>
    ));

  return (
    <div className="sm:hidden">
      <button
        ref={menuButtonRef}
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-content-muted hover:bg-slate-100 hover:text-content-primary transition-colors"
        aria-label="Abrir menu"
        aria-expanded={open}
      >
        <Menu className="h-5 w-5" aria-hidden />
      </button>

      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-50 bg-black/40"
            aria-label="Fechar menu"
            onClick={close}
          />
          <div
            ref={panelRef}
            className="fixed inset-y-0 right-0 z-50 w-[min(100%,280px)] bg-white shadow-xl border-l border-surface-border flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label="Menu de navegação"
          >
            <div className="flex items-center justify-between px-4 h-14 border-b border-surface-border shrink-0">
              <span className="text-sm font-semibold text-content-primary">Menu</span>
              <button
                type="button"
                onClick={close}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-content-muted hover:bg-slate-100 hover:text-content-primary transition-colors"
                aria-label="Fechar menu"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>

            {isUserLayout && (
              <UserContextPanel
                className="border-b border-surface-border shrink-0"
                onWorkspaceSelect={handleWorkspaceSelect}
              />
            )}

            <nav className="flex-1 overflow-y-auto p-3 space-y-1" aria-label="Navegação mobile">
              {isUserLayout ? (
                <>
                  {renderNavLinks(primary)}
                  {secondary.length > 0 && (
                    <div className="pt-2">
                      <div className="px-4 pb-1 text-[11px] font-medium uppercase tracking-wide text-content-muted">
                        Mais
                      </div>
                      {renderNavLinks(secondary)}
                    </div>
                  )}
                </>
              ) : (
                renderNavLinks(adminItems)
              )}
            </nav>

            {!isUserLayout && (
              <div className="p-3 border-t border-surface-border shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    close();
                    onLogout?.();
                  }}
                  className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm text-content-muted hover:bg-slate-50 hover:text-content-primary transition-colors"
                >
                  <LogOut className="h-5 w-5 shrink-0" aria-hidden />
                  Sair
                </button>
              </div>
            )}

            {isUserLayout && (
              <div className="p-3 border-t border-surface-border shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    close();
                    onLogout?.();
                  }}
                  className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm text-content-muted hover:bg-slate-50 hover:text-content-primary transition-colors"
                >
                  <LogOut className="h-5 w-5 shrink-0" aria-hidden />
                  Sair
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default MobileNav;
